/**
 * React Query hook for Case-Specific Client/User List
 * Provides automatic caching, background sync, and optimistic updates
 */

import { useQuery } from '@tanstack/react-query';
import { getClientUserList } from 'services/case/caseServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Fetches client/user list for a specific case or global context
 *
 * @param {Object} params - Query parameters
 * @param {string} params.caseId - Case ID (optional for global)
 * @param {boolean} params.is_contact_list - Include contacts
 * @param {boolean} params.is_team_member_list - Include team members
 * @param {boolean} params.all_contacts - Include all contacts
 * @param {string} params.contact_id - Specific contact ID
 * @param {boolean} params.is_lead - Is lead context
 * @param {boolean} params.is_mandatory_lawft_access - Require lawft access
 * @param {boolean} params.is_active - Include only active users
 * @param {boolean} params.is_include_groups - Include user groups
 * @param {boolean} params.active_groups - Include only active groups
 * @returns {Promise<Array>} - Array of users/clients
 */
const fetchCaseClients = async (params = {}) => {
  const response = await getClientUserList(params);
  return response.data || [];
};

/**
 * Hook to fetch case-specific client/user list with React Query
 *
 * @param {Object} options - Configuration options
 * @param {string} options.caseId - Case ID (optional)
 * @param {boolean} options.is_contact_list - Include contacts (default: false)
 * @param {boolean} options.is_team_member_list - Include team members (default: false)
 * @param {boolean} options.all_contacts - Include all contacts (default: false)
 * @param {string} options.contact_id - Specific contact ID (default: false)
 * @param {boolean} options.is_lead - Is lead context (default: false)
 * @param {boolean} options.is_mandatory_lawft_access - Require lawft access (default: true)
 * @param {boolean} options.is_active - Include only active users (default: false)
 * @param {boolean} options.is_include_groups - Include user groups (default: false)
 * @param {boolean} options.active_groups - Include only active groups (default: false)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 * @param {boolean} options.excludeFromLoading - Exclude from global loading state (default: false)
 *
 * @returns {Object} - clientList, isLoading, error, refetch, isSuccess
 *
 * @example
 * // Fetch team members for a case
 * const { clientList, isLoading } = useCaseClientsQuery({
 *   caseId: 'case123',
 *   is_team_member_list: true,
 *   is_active: true
 * });
 *
 * @example
 * // Fetch all contacts including groups
 * const { clientList } = useCaseClientsQuery({
 *   caseId: 'case123',
 *   all_contacts: true,
 *   is_include_groups: true,
 *   active_groups: true
 * });
 */
export const useCaseClientsQuery = ({
  caseId,
  is_contact_list = false,
  is_team_member_list = false,
  all_contacts = false,
  contact_id = false,
  is_lead = false,
  is_mandatory_lawft_access = true,
  is_active = false,
  is_include_groups = false,
  active_groups = false,
  excludeFromLoading = false,
  enabled = true,
} = {}) => {
  // Create a stable query key based on parameters
  const queryKey = [
    ...queryKeys.users.all,
    'case-clients',
    caseId || 'global',
    {
      is_contact_list,
      is_team_member_list,
      all_contacts,
      contact_id,
      is_lead,
      is_mandatory_lawft_access,
      is_active,
      is_include_groups,
      active_groups,
    },
  ];

  const { data, isLoading, isFetching, error, refetch, isSuccess } = useQuery({
    queryKey,
    queryFn: () =>
      fetchCaseClients({
        caseId,
        is_contact_list,
        is_team_member_list,
        all_contacts,
        contact_id,
        is_lead,
        is_mandatory_lawft_access,
        is_active,
        is_include_groups,
        active_groups,
        excludeFromLoading,
      }),
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes (gcTime replaces cacheTime in v4+)
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 2,
    enabled, // Allow disabling the query
  });

  return {
    clientList: data || [],
    isLoading,
    isFetching,
    error,
    refetch,
    isSuccess,
  };
};
